<template>
  <div class="create-ticket-page">
    <UserAppChrome back-mode="history" />

    <main class="create-ticket-shell">
      <UserSectionHeader
        eyebrow="Assistenza nutrizionale"
        title="Nuova richiesta"
        description="Raccontaci con chiarezza il tuo dubbio: il Team Nutrizionale ti risponderà direttamente nella conversazione."
      />

      <section class="ticket-form-card" aria-labelledby="ticket-form-title">
        <header class="ticket-form-card__header">
          <span class="ticket-form-card__icon" aria-hidden="true">
            <span class="material-icons">forum</span>
          </span>
          <div>
            <p>Canale diretto</p>
            <h2 id="ticket-form-title">Scrivi al team</h2>
          </div>
          <span class="ticket-form-card__limit">2 al mese</span>
        </header>

        <form class="ticket-form" @submit.prevent="createTicket">
          <label class="ticket-field">
            <span class="ticket-field__label">
              <span class="material-icons" aria-hidden="true">title</span>
              Titolo della richiesta
            </span>
            <input
              v-model="title"
              type="text"
              placeholder="Es. Dubbi sul piano alimentare"
              aria-label="Titolo della richiesta"
            />
          </label>

          <label class="ticket-field">
            <span class="ticket-field__label">
              <span class="material-icons" aria-hidden="true">edit_note</span>
              Descrizione
            </span>
            <textarea
              v-model="description"
              rows="7"
              placeholder="Descrivi la situazione, i tuoi dubbi e tutte le informazioni utili…"
              aria-label="Descrizione della richiesta"
            ></textarea>
            <span class="ticket-field__hint">Più dettagli inserisci, più precisa potrà essere la risposta.</span>
          </label>

          <footer class="ticket-form__footer">
            <p>
              <span class="material-icons" aria-hidden="true">schedule</span>
              Hai a disposizione due richieste ogni mese.
            </p>
            <div class="ticket-form__actions">
              <router-link class="ticket-button ticket-button--secondary" to="/ticket-page">
                Le tue richieste
              </router-link>
              <button class="ticket-button ticket-button--primary" type="submit">
                <span>Invia richiesta</span>
                <span class="material-icons" aria-hidden="true">arrow_forward</span>
              </button>
            </div>
          </footer>
        </form>
      </section>

      <aside class="ticket-tip">
        <span class="material-icons" aria-hidden="true">lightbulb</span>
        <div>
          <strong>Un piccolo consiglio</strong>
          <p>Usa un titolo breve e descrivi un solo argomento per richiesta: sarà più semplice seguirne gli aggiornamenti.</p>
        </div>
      </aside>
    </main>
  </div>
</template>

<script>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { getFirestore, addDoc, collection, query, where, getDocs, serverTimestamp, doc, getDoc, updateDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import UserAppChrome from './UserAppChrome.vue';
import UserSectionHeader from './UserSectionHeader.vue';

export default {
  name: 'CreateTicket',
  components: { UserAppChrome, UserSectionHeader },
  setup() {
    const title = ref('');
    const description = ref('');
    const router = useRouter();

    const enableDocumentUploadByDefault = async (db, userId) => {
      const userRef = doc(db, 'users', userId);
      const userSnapshot = await getDoc(userRef);
      if (!userSnapshot.exists()) return;

      if (typeof userSnapshot.data().canUploadNutritionalFiles !== 'boolean') {
        await updateDoc(userRef, { canUploadNutritionalFiles: true });
      }
    };

    const canCreateTicket = async () => {
      const db = getFirestore();
      const auth = getAuth();
      const userId = auth.currentUser.uid;

      // Ottenere l'inizio e la fine del mese corrente
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      // Query per contare i ticket di questo utente in questo mese
      const ticketsQuery = query(
        collection(db, 'tickets'),
        where('userId', '==', userId),
        where('timestamp', '>=', startOfMonth),
        where('timestamp', '<=', endOfMonth)
      );

      const querySnapshot = await getDocs(ticketsQuery);
      return querySnapshot.size < 2; // True se l'utente può ancora creare ticket
    };

    const createTicket = async () => {
      if (!(await canCreateTicket())) {
        alert("Hai raggiunto il limite di 2 ticket al mese.");
        return;
      }

      const db = getFirestore();
      const auth = getAuth();
      const userId = auth.currentUser.uid;

      // Genera il timestamp sul lato client
      const clientTimestamp = new Date().toISOString();

      const conversations = [
        { type: 'Richiesta', content: description.value, timestamp: clientTimestamp }
      ];

      const ticketData = {
        title: title.value,
        status: 'Aperto',
        userId: userId,
        timestamp: serverTimestamp(),
        conversations: conversations
      };

      try {
        const docRef = await addDoc(collection(db, 'tickets'), ticketData);
        await enableDocumentUploadByDefault(db, userId);
        alert('Ticket creato con successo! ID: ' + docRef.id);
        
        // Dopo aver creato con successo il ticket, reindirizza l'utente alla pagina /user
        router.push('/user');
      } catch (error) {
        console.error('Errore nella creazione del ticket:', error);
      }
    };

    return {
      title,
      description,
      createTicket,
    };
  },
};
</script>




<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Montserrat:wght@700;900&display=swap');

.create-ticket-page {
  --ff-bg: #f5f4f0;
  --ff-white: #fff;
  --ff-black: #111110;
  --ff-muted: #66635f;
  --ff-red: #c8102e;
  --ff-red-dark: #990b22;
  position: relative;
  isolation: isolate;
  min-height: 100vh;
  min-height: 100dvh;
  overflow-x: hidden;
  color: var(--ff-black);
  background:
    radial-gradient(circle at 92% 8%, rgba(200, 16, 46, 0.1), transparent 25rem),
    radial-gradient(circle at 3% 74%, rgba(200, 16, 46, 0.05), transparent 28rem),
    var(--ff-bg);
  font-family: 'DM Sans', sans-serif;
  text-align: left;
}

.create-ticket-page,
.create-ticket-page *,
.create-ticket-page *::before,
.create-ticket-page *::after {
  box-sizing: border-box;
}

.create-ticket-page::before {
  position: fixed;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  content: '';
  opacity: 0.24;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 180 180' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='.08'/%3E%3C/svg%3E");
}

.create-ticket-shell {
  width: min(100%, 920px);
  margin: 0 auto;
  padding: clamp(34px, 6vw, 72px) clamp(20px, 5vw, 52px) 148px;
}

.ticket-form-card {
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.94);
  border-radius: 26px;
  background: rgba(255, 255, 255, 0.72);
  box-shadow: 0 24px 58px rgba(47, 38, 32, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(22px) saturate(1.4);
  -webkit-backdrop-filter: blur(22px) saturate(1.4);
}

.ticket-form-card::before {
  position: absolute;
  top: 0;
  right: 8%;
  left: 8%;
  height: 3px;
  content: '';
  background: linear-gradient(90deg, transparent, var(--ff-red), transparent);
}

.ticket-form-card__header {
  display: grid;
  grid-template-columns: 54px minmax(0, 1fr) auto;
  gap: 15px;
  align-items: center;
  padding: 22px clamp(20px, 4vw, 32px);
  border-bottom: 1px solid rgba(17, 17, 16, 0.07);
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.68), rgba(255, 255, 255, 0.24));
}

.ticket-form-card__icon {
  display: grid;
  width: 54px;
  height: 54px;
  place-items: center;
  color: var(--ff-white);
  border-radius: 17px;
  background: linear-gradient(145deg, #d4143b, var(--ff-red-dark));
  box-shadow: 0 12px 26px rgba(158, 11, 35, 0.2);
}

.ticket-form-card__icon .material-icons {
  color: currentColor;
  font-size: 24px;
}

.ticket-form-card__header p,
.ticket-form-card__header h2 {
  margin: 0;
}

.ticket-form-card__header p {
  color: var(--ff-red);
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.ticket-form-card__header h2 {
  margin-top: 3px;
  color: var(--ff-black);
  font-family: 'Montserrat', sans-serif;
  font-size: clamp(1.25rem, 3vw, 1.75rem);
  font-weight: 900;
  line-height: 1;
  letter-spacing: -0.045em;
  text-transform: uppercase;
}

.ticket-form-card__limit {
  padding: 7px 11px;
  color: var(--ff-red-dark);
  border: 1px solid rgba(200, 16, 46, 0.12);
  border-radius: 999px;
  background: rgba(200, 16, 46, 0.07);
  font-size: 0.6875rem;
  font-weight: 700;
  white-space: nowrap;
}

.ticket-form {
  display: grid;
  gap: 20px;
  padding: clamp(22px, 4vw, 34px);
}

.ticket-field {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 9px;
}

.ticket-field__label {
  display: flex;
  align-items: center;
  gap: 7px;
  color: var(--ff-black);
  font-size: 0.78rem;
  font-weight: 700;
}

.ticket-field__label .material-icons {
  color: var(--ff-red);
  font-size: 17px;
}

.ticket-field input,
.ticket-field textarea {
  display: block;
  width: 100%;
  margin: 0;
  padding: 14px 16px;
  color: var(--ff-black);
  border: 1px solid rgba(17, 17, 16, 0.1);
  border-radius: 16px;
  background: rgba(245, 244, 240, 0.78);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
  font: inherit;
  font-size: 0.92rem;
  outline: none;
  transition: border-color 180ms ease, box-shadow 180ms ease, background 180ms ease;
}

.ticket-field textarea {
  min-height: 164px;
  resize: vertical;
  line-height: 1.5;
}

.ticket-field input::placeholder,
.ticket-field textarea::placeholder {
  color: #96918a;
}

.ticket-field input:focus,
.ticket-field textarea:focus {
  border-color: rgba(200, 16, 46, 0.46);
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 0 0 4px rgba(200, 16, 46, 0.08);
}

.ticket-field__hint {
  color: var(--ff-muted);
  font-size: 0.7rem;
  line-height: 1.4;
}

.ticket-form__footer {
  display: flex;
  gap: 18px;
  align-items: center;
  justify-content: space-between;
  padding-top: 2px;
}

.ticket-form__footer > p {
  display: flex;
  max-width: 280px;
  align-items: center;
  gap: 7px;
  margin: 0;
  color: var(--ff-muted);
  font-size: 0.7rem;
  line-height: 1.35;
}

.ticket-form__footer > p .material-icons {
  color: var(--ff-red);
  font-size: 17px;
}

.ticket-form__actions {
  display: flex;
  gap: 9px;
  align-items: center;
}

.ticket-button {
  display: inline-flex;
  min-height: 45px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0;
  padding: 11px 17px;
  border-radius: 999px;
  font: inherit;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1;
  text-decoration: none;
  cursor: pointer;
  appearance: none;
  transition: transform 180ms ease, box-shadow 180ms ease, background 180ms ease;
}

.ticket-button--secondary {
  color: var(--ff-black);
  border: 1px solid rgba(17, 17, 16, 0.11);
  background: rgba(255, 255, 255, 0.65);
}

.ticket-button--primary {
  color: var(--ff-white);
  border: 1px solid var(--ff-red);
  background: var(--ff-red);
  box-shadow: 0 12px 24px rgba(200, 16, 46, 0.2);
}

.ticket-button--primary .material-icons {
  color: currentColor;
  font-size: 17px;
}

.ticket-button:hover {
  transform: translateY(-1px);
}

.ticket-button--primary:hover {
  background: var(--ff-red-dark);
  box-shadow: 0 15px 28px rgba(158, 11, 35, 0.25);
}

.ticket-tip {
  display: flex;
  gap: 13px;
  align-items: flex-start;
  margin-top: 16px;
  padding: 17px 19px;
  color: var(--ff-muted);
  border: 1px solid rgba(255, 255, 255, 0.86);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.46);
  box-shadow: 0 12px 28px rgba(47, 38, 32, 0.06);
}

.ticket-tip > .material-icons {
  color: var(--ff-red);
  font-size: 21px;
}

.ticket-tip strong {
  color: var(--ff-black);
  font-size: 0.78rem;
}

.ticket-tip p {
  margin: 3px 0 0;
  font-size: 0.72rem;
  line-height: 1.45;
}

.create-ticket-page :is(input, textarea, button, a):focus-visible {
  outline: 3px solid rgba(200, 16, 46, 0.28);
  outline-offset: 3px;
}

@media (max-width: 680px) {
  .create-ticket-shell {
    padding: 28px 12px 138px;
  }

  .ticket-form-card {
    border-radius: 22px;
  }

  .ticket-form-card__header {
    grid-template-columns: 46px minmax(0, 1fr) auto;
    gap: 11px;
    padding: 17px 15px;
  }

  .ticket-form-card__icon {
    width: 46px;
    height: 46px;
    border-radius: 14px;
  }

  .ticket-form-card__limit {
    padding: 6px 8px;
    font-size: 0.6875rem;
  }

  .ticket-form {
    gap: 17px;
    padding: 18px 15px 16px;
  }

  .ticket-field textarea {
    min-height: 150px;
  }

  .ticket-form__footer {
    flex-direction: column;
    align-items: stretch;
  }

  .ticket-form__footer > p {
    max-width: none;
  }

  .ticket-form__actions {
    display: grid;
    grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
  }

  .ticket-button {
    min-width: 0;
    padding-right: 11px;
    padding-left: 11px;
    text-align: center;
  }

  .ticket-tip {
    margin: 13px 7px 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .ticket-field input,
  .ticket-field textarea,
  .ticket-button {
    transition: none;
  }
}
</style>
